import { useEffect, useRef } from 'react';
import { TVideoSource } from '@/modules/content/creation/model/types';
import { getYouTubeDurationValidationError } from '@/modules/content/creation/model/youtube';

type Params = {
  source: TVideoSource;
  youtubeUrl: string;
  onErrorChange?: (message: string | null) => void;
  onCheckingChange?: (checking: boolean) => void;
};

const YOUTUBE_DURATION_VALIDATION_DEBOUNCE_MS = 500;

export const useYouTubeDurationValidation = ({
  source,
  youtubeUrl,
  onErrorChange,
  onCheckingChange,
}: Params) => {
  const validationIdRef = useRef(0);
  const onErrorChangeRef = useRef(onErrorChange);
  const onCheckingChangeRef = useRef(onCheckingChange);

  useEffect(() => {
    onErrorChangeRef.current = onErrorChange;
    onCheckingChangeRef.current = onCheckingChange;
  }, [onErrorChange, onCheckingChange]);

  useEffect(() => {
    if (source !== 'youtube') {
      onCheckingChangeRef.current?.(false);
      return;
    }

    const trimmedUrl = youtubeUrl.trim();
    const validationId = ++validationIdRef.current;

    if (!trimmedUrl) {
      onCheckingChangeRef.current?.(false);
      onErrorChangeRef.current?.(null);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      onCheckingChangeRef.current?.(true);

      try {
        const message = await getYouTubeDurationValidationError(trimmedUrl);

        if (validationId === validationIdRef.current) {
          onErrorChangeRef.current?.(message);
        }
      } catch {
        if (validationId === validationIdRef.current) {
          onErrorChangeRef.current?.('We could not check this YouTube video. Please try another link.');
        }
      } finally {
        if (validationId === validationIdRef.current) {
          onCheckingChangeRef.current?.(false);
        }
      }
    }, YOUTUBE_DURATION_VALIDATION_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      validationIdRef.current += 1;
    };
  }, [source, youtubeUrl]);
};
